import type { ReactNode } from 'react';

/**
 * Flat line icons for notice banners (24 x 24 grid, 2px stroke, current text colour), drawn
 * in SVG so they look the same on every device instead of as glossy system emoji.
 * Decorative by default: pass `label` when the icon carries meaning on its own.
 */
export type IconName = 'info' | 'check-circle' | 'warning' | 'error' | 'offline';

const paths: Record<IconName, ReactNode> = {
  info: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v6M12 7.5v.5" />
    </>
  ),
  'check-circle': (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M8 12.5l3 3 5-6" />
    </>
  ),
  warning: (
    <>
      <path d="M12 3.5 21.5 20h-19Z" />
      <path d="M12 10v4.5M12 17.5v.5" />
    </>
  ),
  error: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M9 9l6 6M15 9l-6 6" />
    </>
  ),
  offline: (
    <>
      <path d="M3 3l18 18" />
      <path d="M8.5 16.5a5 5 0 0 1 7 0M5 13a10 10 0 0 1 5-2.7M14 10.3A10 10 0 0 1 19 13M2 9.5A15 15 0 0 1 7 6.7M12 5a15 15 0 0 1 10 4.5" />
      <path d="M12 20v.5" />
    </>
  ),
};

export function Icon({
  name,
  size = '1.15em',
  label,
}: {
  name: IconName;
  size?: number | string;
  label?: string;
}) {
  return (
    <svg
      className="icon"
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      focusable="false"
      {...(label ? { role: 'img', 'aria-label': label } : { 'aria-hidden': true })}
    >
      {paths[name]}
    </svg>
  );
}
